import mitt from '@livechat/mitt';
import platformClient from '@livechat/platform-client';
import { generateRandomId } from '@livechat/data-utils';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

// RTM API EVENTS:
var LOGIN = "login";
var SEND_EVENT = "send_event";
var READY = "ready"; // WEBSOCKET:

var CONNECT = "connect";
var DISCONNECT = "disconnect";
var MESSAGE = "message";
var PUSH_MESSAGE = "push"; // SOCKET LOGS:

var LOG_RESPONSE = "Receive response: ";
var LOG_PUSH = "Receive push: ";
var LOG_SEND = "Send: "; // REQUESTS:

var requestTimeoutError = function requestTimeoutError(timeout, action) {
  return "Action" + (action ? ": '" + action + "'" : " ") + " was rejected by timeout (" + timeout + " ms).";
}; // ERRORS:

var NOT_SUPPORTED_API_VERSION = "ChatSDK: Provided API version is no longer supported";
var API_NOT_READY = "ChatSDK: API is not ready yet";
var AGENT_NOT_LOGGED_IN = "ChatSDK.getAgentDetails: Agent is not logged in yet";
var SOCKET_ALREADY_OPEN = "ChatSDK: Socket is already open";
var SOCKET_DISCONNECT_ERROR = "ChatSDK: Rejected all pending request due to WebSocket disconnection";
var ERROR_INIT_MISSING_CONFIG = "ChatSDK.init: Missing config";
var ERROR_INIT_MISSING_TOKEN = "ChatSDK.init: Incorrect config, missing token value";
var ERROR_METHOD_FACTORY_INCORRECT_PARAMS = "ChatSDK.methodFactory: Incorrect requestBody parameter";
var ERROR_SEND_MESSAGE_MISSING_CHAT_ID = "ChatSDK.sendMessage: Missing chat_id param";
var ERROR_SEND_MESSAGE_MISSING_MESSAGE = "ChatSDK.sendMessage: Missing message"; // DEFAULT SKD CONFIG

var defaultConfig = {
  apiVersion: "3.4"
};

var WEB_SOCKET_CYCLE = 15; // sec

var DEFAULT_REQUEST_TIMEOUT = (WEB_SOCKET_CYCLE + 5) * 1000;
var defaultOptions = {
  timeout: DEFAULT_REQUEST_TIMEOUT,
  timeoutReason: requestTimeoutError(DEFAULT_REQUEST_TIMEOUT)
};

var RequestController = /*#__PURE__*/function () {
  function RequestController(options) {
    var _this = this;

    if (options === void 0) {
      options = {};
    }

    this._handleTimeout = function () {
      _this.timeout = setTimeout(function () {
        return _this.reject(_this.options.timeoutReason);
      }, _this.options.timeout);
      return _this.timeout;
    };

    this.options = _extends({}, defaultOptions, options);
    this._promise = null;
    this._resolve = null;
    this._reject = null;
    this._isPending = false;
    this._isSettled = false;
    this.timeout = null;
  }

  var _proto = RequestController.prototype;

  _proto._createPromise = function _createPromise() {
    var _this2 = this;

    this._promise = new Promise(function (resolve, reject) {
      _this2._isPending = true;
      _this2._resolve = resolve;
      _this2._reject = reject;
    });
  };

  _proto._callAction = function _callAction() {
    if (this.options.action && typeof this.options.action === "function") {
      try {
        this.options.action();
      } catch (e) {
        this.reject(e);
      }
    }
  } // #### PUBLIC METHODS ####
  ;

  _proto.start = function start() {
    if (!this._isPending) {
      this._createPromise();

      this._callAction();
    }

    Promise.race([this._promise, this._handleTimeout()]);
    return this._promise;
  };

  _proto.resolve = function resolve(value) {
    if (this._isPending) {
      this._isPending = false;
      this._isSettled = true;

      this._resolve(value);

      clearTimeout(this.timeout);
    }
  };

  _proto.reject = function reject(value) {
    if (this._isPending) {
      this._isPending = false;
      this._isSettled = true;

      this._reject(value);

      clearTimeout(this.timeout);
    }
  };

  return RequestController;
}();

var Requests = /*#__PURE__*/function () {
  function Requests(socketEmitter) {
    this._socketEmitter = socketEmitter;
    this._items = new Map();
  }

  var _proto = Requests.prototype;

  _proto._promiseFinally = function _promiseFinally(promise, callback) {
    return promise["finally"](callback);
  };

  _proto._deleteRequest = function _deleteRequest(requestId) {
    this._items["delete"](requestId);
  };

  _proto._generateUniqueId = function _generateUniqueId() {
    var id = generateRandomId();
    return this._items.has(id) ? this._generateUniqueId() : id;
  } // ##### PUBLIC METHODS ######
  ;

  _proto.create = function create(requestBody, timeout) {
    var _this = this;

    if (timeout === void 0) {
      timeout = DEFAULT_REQUEST_TIMEOUT;
    }

    var requestId = this._generateUniqueId();

    var payload = _extends({
      request_id: requestId
    }, requestBody);

    var request = new RequestController({
      timeout: timeout,
      timeoutReason: requestTimeoutError(timeout, payload.action),
      action: function action() {
        return _this._socketEmitter(payload);
      }
    });

    this._items.set(requestId, request);

    return this._promiseFinally(request.start(), function () {
      return _this._deleteRequest(requestId);
    });
  };

  _proto.resolve = function resolve(requestId, data) {
    if (requestId && this._items.has(requestId)) {
      this._items.get(requestId).resolve(data);
    }
  };

  _proto.reject = function reject(requestId, error) {
    if (requestId && this._items.has(requestId)) {
      this._items.get(requestId).reject(error);
    }
  };

  _proto.rejectAll = function rejectAll(error) {
    this._items.forEach(function (request) {
      return request.reject(error);
    });
  };

  return Requests;
}();

function checkIfApiVersionIsSupported(apiVersion) {
  if (!apiVersion) {
    return null;
  }

  apiVersion = apiVersion.includes("v") ? Number(apiVersion.substring(1)) : Number(apiVersion);
  var supportedVersion = Number("3.4");

  if (apiVersion < supportedVersion) {
    console.warn(NOT_SUPPORTED_API_VERSION);
  }
}

function validateConfig(config) {
  if (!config) {
    throw new Error(ERROR_INIT_MISSING_CONFIG);
  } else if (!config.access_token && !config.personal_access_token) {
    throw new Error(ERROR_INIT_MISSING_TOKEN);
  } else {
    return true;
  }
}
function getRtmUrl(_temp) {
  var _ref = _temp === void 0 ? {} : _temp,
      region = _ref.region,
      apiVersion = _ref.apiVersion;

  // eslint-disable-next-line
  var versionRegex = new RegExp(/\/v([\d\.]*)\//);
  var url = region && region.toLowerCase() === "europe" ? "https://api-fra.livechatinc.com/v3.4/agent/rtm/sjs" : "https://api.livechatinc.com/v3.4/agent/rtm/sjs";

  if (apiVersion) {
    checkIfApiVersionIsSupported(apiVersion);
    url = url.replace(versionRegex, function () {
      var shouldIncludeVersionTag = !apiVersion.includes("v");
      return "/" + (shouldIncludeVersionTag ? "v" : "") + apiVersion + "/";
    });
  }

  return url;
}

var SocketClient = function SocketClient(_ref) {
  var _this = this;

  var sdkEmitter = _ref.sdkEmitter,
      platformEmitter = _ref.platformEmitter,
      config = _ref.config;

  this._handleIncomingEvents = function () {
    _this.client.on(CONNECT, function () {
      _this.isConnected = true;
    });

    _this.client.on(DISCONNECT, function () {
      _this.isConnected = false;

      _this.requests.rejectAll(SOCKET_DISCONNECT_ERROR);
    });

    _this.client.on(MESSAGE, function (message) {
      if (message.request_id) {
        if (_this.config.debug) {
          console.log(LOG_RESPONSE, message);
        } // handle responses


        if (message.success) {
          _this.requests.resolve(message.request_id, message);
        } else {
          _this.requests.reject(message.request_id, message);
        }
      } // handle push messages


      if (message.type === PUSH_MESSAGE) {
        if (message && message.action) {
          if (_this.config.debug) {
            console.log(LOG_PUSH, message);
          }

          _this._sdkEmitter.emit(message.action, message);
        }
      }
    });
  };

  this.checkIsRTMReady = function () {
    return _this.client.getReadyState() === 1;
  };

  this.init = function () {
    if (_this.isConnected) {
      console.warn(SOCKET_ALREADY_OPEN);
    } else {
      _this.client.connect();
    }
  };

  this.send = function (requestBody, timeout) {
    if (_this.config.debug) {
      console.log(LOG_SEND, requestBody);
    }

    return _this.requests.create(requestBody, timeout);
  };

  this.destroy = function () {
    _this.client.destroy();

    _this._platformEmitter.off();
  };

  this.config = config;
  this._sdkEmitter = sdkEmitter;
  this._platformEmitter = platformEmitter;
  this.client = platformClient(getRtmUrl(config), {
    emitter: this._platformEmitter
  });

  this._handleIncomingEvents();

  this.isConnected = false;
  this.requests = new Requests(this.client.emit);
};

var SDK = function SDK(config) {
  var _this = this;

  if (config === void 0) {
    config = {};
  }

  this._checkRtmConnection = function () {
    return _this._AGENT_API_RTM.checkIsRTMReady();
  };

  this._promisify = function (func) {
    return new Promise(function (resolve, reject) {
      if (_this._checkRtmConnection()) {
        return func(resolve, reject);
      } else {
        reject(API_NOT_READY);
      }
    });
  };

  this._getToken = function () {
    if (_this._authConfig.personal_access_token) {
      return "Basic " + _this._authConfig.personal_access_token;
    } else if (_this._authConfig.access_token) {
      return "Bearer " + _this._authConfig.access_token;
    }

    return null;
  };

  this._login = function () {
    var requestBody = {
      action: LOGIN,
      payload: {
        token: _this._getToken()
      }
    };
    return _this.methodFactory(requestBody);
  };

  this._eventListeners = function () {
    _this.platformEmitter.on(CONNECT, function () {
      _this._login().then(function (data) {
        _this._isLoggedIn = true;
        _this._agentDetails = data;

        _this.sdkEmitter.emit(READY);
      })["catch"](function (err) {
        _this._isLoggedIn = false;

        if (_this.config.debug) {
          console.warn(err);
        }
      });
    });
  };

  this.init = function (authConfig) {
    validateConfig(authConfig);
    _this._authConfig = authConfig;

    _this._eventListeners();

    _this._AGENT_API_RTM.init();
  };

  this.destroy = function () {
    _this._AGENT_API_RTM.destroy();
  };

  this.methodFactory = function (requestBody, timeout) {
    return _this._promisify(function (resolve, reject) {
      if (!requestBody || requestBody && !requestBody.action) {
        reject(ERROR_METHOD_FACTORY_INCORRECT_PARAMS);
      }

      _this._AGENT_API_RTM.send(requestBody, timeout).then(function (data) {
        return resolve(data && data.payload);
      })["catch"](function (err) {
        return reject(err);
      });
    });
  };

  this.getAgentDetails = function () {
    return _this._promisify(function (resolve, reject) {
      if (_this._isLoggedIn) {
        resolve(_this._agentDetails);
      } else {
        reject(AGENT_NOT_LOGGED_IN);
      }
    });
  };

  this.sendMessage = function (chat_id, message, recipients) {
    if (recipients === void 0) {
      recipients = "all";
    }

    if (!chat_id) throw new Error(ERROR_SEND_MESSAGE_MISSING_CHAT_ID);
    if (!message) throw new Error(ERROR_SEND_MESSAGE_MISSING_MESSAGE);
    return _this.methodFactory({
      action: SEND_EVENT,
      payload: {
        chat_id: chat_id,
        event: {
          type: "message",
          text: message,
          recipients: recipients
        }
      }
    });
  };

  this.on = function () {
    var _this$sdkEmitter;

    return (_this$sdkEmitter = _this.sdkEmitter).on.apply(_this$sdkEmitter, arguments);
  };

  this.once = function () {
    var _this$sdkEmitter2;

    return (_this$sdkEmitter2 = _this.sdkEmitter).once.apply(_this$sdkEmitter2, arguments);
  };

  this.off = function () {
    var _this$sdkEmitter3;

    return (_this$sdkEmitter3 = _this.sdkEmitter).off.apply(_this$sdkEmitter3, arguments);
  };

  this.config = Object.assign({}, defaultConfig, config);
  this.sdkEmitter = mitt();
  this.platformEmitter = mitt();
  this._AGENT_API_RTM = new SocketClient({
    sdkEmitter: this.sdkEmitter,
    platformEmitter: this.platformEmitter,
    config: config
  });
  this._agentDetails = {};
  this._authConfig = null;
  this._isLoggedIn = false;
};

export default SDK;
